package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AudiobookDownloadStatus
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun DownloadProgressPanel(
    apiClient: ApiClient,
    audiobookId: String
) {
    val colors = AppTheme.colors
    var status by remember { mutableStateOf<AudiobookDownloadStatus?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var retrying by remember { mutableStateOf(false) }
    // Bumped to restart polling after a retry queues new work.
    var pollGeneration by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    // Auto-refresh while downloads are in progress. Polling stops only once nothing is
    // outstanding — it used to also break on "partial_failure", which meant a single
    // failed chapter froze the panel and later real progress was never picked up.
    LaunchedEffect(audiobookId, pollGeneration) {
        while (true) {
            try {
                val result = apiClient.getAudiobookDownloadStatus(audiobookId)
                status = result
                error = null
                if (result.pendingChapters == 0 && result.downloadingChapters == 0) {
                    break
                }
            } catch (e: Exception) {
                error = e.message
                break
            }
            delay(3000)
        }
    }

    val currentStatus = status ?: return

    // Only show if there are downloads
    if (currentStatus.totalChapters == 0) return

    // Don't show if everything is already complete
    if (currentStatus.overallStatus == "complete" && currentStatus.failedChapters == 0) return

    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            padding(Spacing.md)
            marginBottom(Spacing.md)
            themeTransition()
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.sm)
            }
        }) {
            Div({
                style {
                    fontSize(Typography.base)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) { Text("Audio Downloads") }

            val statusText = when (currentStatus.overallStatus) {
                "complete" -> "Complete"
                "partial_failure" -> "Partial Failure"
                "in_progress" -> "Downloading..."
                "pending" -> "Queued"
                else -> currentStatus.overallStatus
            }
            val statusColor = when (currentStatus.overallStatus) {
                "complete" -> colors.success
                "partial_failure" -> colors.warning
                "in_progress" -> colors.primary
                else -> colors.textMuted
            }
            Span({
                style {
                    fontSize(13.px)
                    fontWeight("500")
                    color(Color(statusColor))
                }
            }) { Text(statusText) }
        }

        // Progress bar
        val progress = if (currentStatus.totalChapters > 0) {
            currentStatus.completedChapters.toDouble() / currentStatus.totalChapters.toDouble()
        } else 0.0

        Div({
            style {
                width(100.percent)
                height(6.px)
                backgroundColor(Color(colors.border))
                borderRadius(3.px)
                overflow("hidden")
                marginBottom(Spacing.sm)
            }
        }) {
            Div({
                style {
                    height(100.percent)
                    width((progress * 100).percent)
                    backgroundColor(Color(
                        if (currentStatus.failedChapters > 0) colors.warning else colors.success
                    ))
                    property("transition", "width 0.5s ease")
                }
            })
        }

        // Counts
        Div({
            style {
                fontSize(13.px)
                color(Color(colors.textMuted))
                display(DisplayStyle.Flex)
                gap(Spacing.md)
            }
        }) {
            Span { Text("${currentStatus.completedChapters}/${currentStatus.totalChapters} complete") }
            if (currentStatus.downloadingChapters > 0) {
                Span { Text("${currentStatus.downloadingChapters} downloading") }
            }
            if (currentStatus.pendingChapters > 0) {
                Span { Text("${currentStatus.pendingChapters} pending") }
            }
            if (currentStatus.failedChapters > 0) {
                Span({ style { color(Color(colors.danger)) } }) {
                    Text("${currentStatus.failedChapters} failed")
                }
            }
        }

        // Failed chapter details
        val failedChapters = currentStatus.chapters.filter { it.downloadStatus == "failed" }
        if (failedChapters.isNotEmpty()) {
            Div({
                style {
                    marginTop(Spacing.sm)
                    maxHeight(100.px)
                    overflowY("auto")
                }
            }) {
                failedChapters.forEach { chapter ->
                    Div({
                        style {
                            fontSize(12.px)
                            color(Color(colors.danger))
                            marginBottom(2.px)
                        }
                    }) {
                        Text("Ch. ${chapter.episodeNumber}: ${chapter.downloadError ?: "Download failed"}")
                    }
                }
            }
        }

        // Retry / refresh actions
        Div({
            style {
                marginTop(Spacing.sm)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
                gap(Spacing.sm)
            }
        }) {
            Button({
                onClick {
                    scope.launch {
                        try {
                            status = apiClient.getAudiobookDownloadStatus(audiobookId)
                        } catch (e: Exception) {
                            error = e.message
                        }
                    }
                }
                style {
                    padding(4.px, Spacing.md)
                    fontSize(12.px)
                    fontWeight("500")
                    color(Color(colors.textMuted))
                    backgroundColor(Color(colors.surface))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    cursor("pointer")
                }
            }) { Text("Refresh Status") }

            // Actually requeues the work, unlike the old button of this name which only
            // re-read the same stale rows.
            if (currentStatus.failedChapters > 0 || currentStatus.pendingChapters > 0) {
                Button({
                    if (retrying) attr("disabled", "true")
                    onClick {
                        scope.launch {
                            retrying = true
                            try {
                                status = apiClient.retryAudiobookDownloads(audiobookId).status
                                error = null
                                pollGeneration++
                            } catch (e: Exception) {
                                error = e.message
                            } finally {
                                retrying = false
                            }
                        }
                    }
                    style {
                        padding(4.px, Spacing.md)
                        fontSize(12.px)
                        fontWeight("500")
                        color(Color(colors.primary))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.primary))
                        borderRadius(BorderRadius.sm)
                        cursor(if (retrying) "default" else "pointer")
                    }
                }) { Text(if (retrying) "Retrying…" else "Retry Failed") }
            }
        }
    }
}
